import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiHata } from '../lib/api';

/**
 * PAROLA BELİRLEME — maildeki bağlantının açtığı ekran.
 *
 * OTURUMSUZ ÇALIŞIR: davet bağlantısına tıklayan kişinin henüz kullanabildiği
 * bir hesabı yoktur. Bu yüzden Kabuk bu yolu giriş kapısının DIŞINDA tutar.
 *
 * İki amaca da hizmet eder — davet (yeni hesap) ve sıfırlama (unutulan şifre);
 * ayrımı sunucudan gelen `tip` belirler, yalnız metinler değişir.
 */
interface JetonBilgi {
  gecerli: boolean;
  ad?: string;
  tip?: 'davet' | 'sifirlama';
}

export function ParolaBelirle({ jeton, onGiris }: { jeton: string; onGiris: () => void }) {
  const [bilgi, setBilgi] = useState<JetonBilgi | null>(null);
  const [yeni, setYeni] = useState('');
  const [tekrar, setTekrar] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const [bitti, setBitti] = useState(false);

  useEffect(() => {
    api
      .get<JetonBilgi>(`/auth/parola-jetonu/${encodeURIComponent(jeton)}`)
      .then(setBilgi)
      .catch(() => setBilgi({ gecerli: false }));
  }, [jeton]);

  const zayif = yeni.length > 0 && yeni.length < 8;
  const uyusmuyor = tekrar.length > 0 && yeni !== tekrar;

  async function gonder(e: FormEvent) {
    e.preventDefault();
    setHata(null);
    setMesgul(true);
    try {
      await api.post('/auth/parola-belirle', { jeton, yeniParola: yeni });
      setBitti(true);
    } catch (err) {
      setHata(err instanceof ApiHata ? err.message : 'Şifre belirlenemedi. Tekrar deneyin.');
      setMesgul(false);
    }
  }

  const kutu =
    'w-full max-w-sm rounded-(--radius-mod) border border-line bg-surface p-6 shadow-(--shadow-mod)';
  const sarmal = 'flex min-h-dvh items-center justify-center bg-surface-2 p-4';
  const gir =
    'w-full rounded-(--radius-mod) border border-line-2 px-3 py-2.5 text-base outline-none focus:border-navy';

  if (bilgi === null) {
    return (
      <main className={sarmal}>
        <p className="text-sm text-wait">Yükleniyor…</p>
      </main>
    );
  }

  if (!bilgi.gecerli) {
    return (
      <main className={sarmal}>
        <div className={kutu}>
          <h1 className="text-xl font-semibold text-navy">Bağlantı geçersiz</h1>
          <p className="mt-3 text-sm leading-relaxed text-wait">
            Bu bağlantının süresi dolmuş ya da daha önce kullanılmış. Şifre belirleme
            bağlantıları <strong>bir kez</strong> kullanılabilir.
          </p>
          <p className="mt-2 text-sm leading-relaxed text-wait">
            Giriş ekranındaki <strong>“Şifremi unuttum”</strong> ile yeni bir bağlantı
            isteyebilirsiniz.
          </p>
          <button
            onClick={onGiris}
            className="mt-5 w-full rounded-(--radius-mod) bg-navy px-4 py-2.5 font-medium text-white"
          >
            Giriş ekranına git
          </button>
        </div>
      </main>
    );
  }

  if (bitti) {
    return (
      <main className={sarmal}>
        <div className={kutu}>
          <h1 className="text-xl font-semibold text-ok">Şifreniz belirlendi ✓</h1>
          <p className="mt-3 text-sm leading-relaxed text-wait">
            Artık <strong>telefon numaranız ve yeni şifrenizle</strong> giriş yapabilirsiniz.
          </p>
          <button
            onClick={onGiris}
            className="mt-5 w-full rounded-(--radius-mod) bg-navy px-4 py-2.5 font-medium text-white"
          >
            Giriş yap
          </button>
        </div>
      </main>
    );
  }

  const davet = bilgi.tip === 'davet';

  return (
    <main className={sarmal}>
      <form onSubmit={gonder} className={kutu}>
        <h1 className="text-xl font-semibold text-navy">
          {davet ? 'Hoş geldiniz' : 'Yeni şifre belirle'}
        </h1>
        <p className="mt-1 mb-5 text-sm leading-relaxed text-wait">
          {bilgi.ad} — {davet ? 'Mod ERP hesabınız açıldı. ' : ''}
          Kendinize bir şifre belirleyin. Uygulamaya <strong>telefon numaranızla</strong>{' '}
          gireceksiniz.
        </p>

        <label className="mb-1 block text-sm font-medium text-ink-2" htmlFor="yeni-parola">
          Yeni şifre
        </label>
        <input
          id="yeni-parola"
          className={gir}
          type="password"
          autoComplete="new-password"
          autoFocus
          value={yeni}
          onChange={(e) => setYeni(e.target.value)}
          required
        />
        <p className={`mt-1 mb-4 text-xs ${zayif ? 'text-crit' : 'text-wait'}`}>En az 8 karakter.</p>

        <label className="mb-1 block text-sm font-medium text-ink-2" htmlFor="yeni-parola-tekrar">
          Yeni şifre tekrar
        </label>
        <input
          id="yeni-parola-tekrar"
          className={gir}
          type="password"
          autoComplete="new-password"
          value={tekrar}
          onChange={(e) => setTekrar(e.target.value)}
          required
        />
        {uyusmuyor && <p className="mt-1 text-xs text-crit">Şifreler uyuşmuyor.</p>}

        {hata && (
          <p className="mt-4 rounded-(--radius-mod) bg-crit/10 p-3 text-sm text-crit">{hata}</p>
        )}

        <button
          disabled={mesgul || zayif || uyusmuyor || !yeni || !tekrar}
          className="mt-5 w-full rounded-(--radius-mod) bg-navy px-4 py-2.5 font-medium text-white disabled:opacity-40"
        >
          {mesgul ? 'Kaydediliyor…' : 'Şifreyi belirle'}
        </button>
      </form>
    </main>
  );
}
